import React, { useState, useEffect, useCallback } from 'react';
import { styled, ThemeProvider } from '@mui/material/styles';
import { Paper, Typography, Slider, Box, Button, Stack, IconButton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import styles from './styles';
import { LightTheme } from '@mercedes-benz/mbui-theme';
import { AddCircleOutlineOutlined, RemoveCircleOutlineOutlined } from '@mui/icons-material';
/*
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { v4 as uuidv4 } from 'uuid';
*/

const ConfigAreaRoot = styled(Paper)(styles);

const ConfigArea = ({
    matchedConfiguration,
    updateConfiguration,
    configurations,
}) => {
    const MIN_FCE = 1;
    const MAX_FCE = 3;
    const DEFAULT_FCE = 1;
    const STEP_FCE = 0.5;
    const STEP_MARKS_FCE = 1;

    const configValues = Object.values(configurations);

    const getMinMaxValues = (configurations) => {
        let maxPower = -Infinity;
        let minPower = Infinity;
        let maxCapacity = -Infinity;
        let minCapacity = Infinity;

        for (const key in configurations) {
            if (configurations.hasOwnProperty(key)) {
                const config = configurations[key];
                if (config.power > maxPower) {
                    maxPower = config.power;
                }
                if (config.power < minPower) {
                    minPower = config.power;
                }
                if (config.capacity > maxCapacity) {
                    maxCapacity = config.capacity;
                }
                if (config.capacity < minCapacity) {
                    minCapacity = config.capacity;
                }
            }
        }

        return { minPower, maxPower, minCapacity, maxCapacity };
    }
    const { minPower, maxPower, minCapacity, maxCapacity } = getMinMaxValues(configurations);

    //const bodyRef = React.useRef(document.getElementsByTagName('body')[0]);
    const [paramPower, setParamPower] = useState(minPower);
    const [paramCapacity, setParamCapacity] = useState(minCapacity);
    const [paramFce, setParamFce] = useState(DEFAULT_FCE);
    const [resultPower, setResultPower] = useState('--- MW');
    const [resultCapacity, setResultCapacity] = useState(`--- MWh`);
    const [resultProductName, setResultProductName] = useState('---');
    const [capacityEnabled, setCapacityEnabled] = useState(false);

    const formatCapacityValue = (value) => {
        return `${parseFloat(value).toLocaleString("de-DE", {
            useGrouping: true,
            maximumFractionDigits: 3,
            minimumFractionDigits: 2
        })}`;
    }

    const formatPowerValue = (value) => {
        return `${parseFloat(value).toLocaleString("de-DE", {
            useGrouping: true,
            maximumFractionDigits: 2,
            minimumFractionDigits: 1
        })}`;
    }

    const formatFceValue = (value) => {
        return `${parseFloat(value).toLocaleString("de-DE", {
            useGrouping: true,
            maximumFractionDigits: 1,
            minimumFractionDigits: 0
        })}`;
    }

    const generateMarks = (property) => {
        var marks = {};
        const maxInnerValues = (property === 'capacity') ? 3 : 4;

        configValues.forEach(config => {
            if (marks[config[property].toString()]) return;
            marks[config[property].toString()] = {
                value: config[property],
                label: '' // Default to empty label
            };
        });

        let markValues = Object.values(marks);
        // remove duplicate values by numeric comparison
        markValues.sort((a, b) => a.value - b.value); // Ensure marks are sorted by value
        
        const uniqueMarkValues = [];
        const seenValues = new Set();
        markValues.forEach(mark => {
            if (!seenValues.has(mark.value)) {
                uniqueMarkValues.push(mark);
                seenValues.add(mark.value);
            }
        });

        markValues = uniqueMarkValues;

        var marksFiltered = [];
        if (markValues.length > 0) {
            marksFiltered.push(markValues[0]); // First mark
            marksFiltered.push(markValues[markValues.length - 1]); // Last mark

            if (markValues.length > 2) {
                const numMiddleMarks = Math.min(maxInnerValues, markValues.length - 2); // Up to 3 middle marks
                const interval = Math.floor((markValues.length - 1) / (numMiddleMarks));

                for (let i = 1; i <= numMiddleMarks; i++) {
                    marksFiltered.push(markValues[i * interval]);
                }
            }
        }

        // Set labels for the filtered marks
        marksFiltered.forEach(mark => {
            var label = '';
            if( property === 'capacity' ) label = formatCapacityValue(mark.value);
            else if ( property === 'power' ) label = formatPowerValue(mark.value);

            marks[mark.value.toString()].label = label;
        });

        return Object.values(marks)
            .sort((a, b) => a.value - b.value);
    };

    const _generateMarksFce = () => {
        var marks = []
        for (let index = (MIN_FCE + 0); index <= (MAX_FCE - 0); index += STEP_MARKS_FCE) {
            marks.push({
                value: index,
                label: index.toLocaleString("de")
            })
        }

        return marks
    }

    const marksCapacity = generateMarks('capacity');
    const marksPower = generateMarks('power');
    const marksFce = _generateMarksFce();

    /*const preparePrintVersion = () => {
        if (bodyRef.current) {
            bodyRef.current.classList.add('print');
        }
    }

    const resetPrintVersion = () => {
        if (bodyRef.current) {
            bodyRef.current.classList.remove('print');
        }
    }

    const generatePDF = (event) => {
        preparePrintVersion();

        setTimeout(() => {
            const input = document.getElementsByTagName('html')[0];
            const options = {
                scale: 2, // Increase scale for better quality
                useCORS: true, // Enable CORS if your application loads images from other domains
                scrollX: 0,
                scrollY: 0,
                windowWidth: 820,
                windowHeight: 1180,
            }

            html2canvas(input, options).then((canvas) => {
                const imgData = canvas.toDataURL('image/png');
                resetPrintVersion();

                const pdf = new jsPDF({
                    orientation: 'portrait',
                    unit: 'px',
                    format: [410, 590], // Set the format to match iPad Pro portrait mode
                });
                pdf.addImage(imgData, 'PNG', 0, 0, 410, 590, 'image', 'FAST');
                pdf.save(`BESS${matchedConfiguration.productName}_${uuidv4()}.pdf`);
            });
        }, 100);
    };*/

    const onPowerChange = (event, value) => {
        setParamPower(value);
        const capacities = getPossibleCapacities(value);
        if (capacities.length > 0) {
            setCapacityEnabled(true);
            const foundIndex = capacities.findIndex(entry => entry === paramCapacity );
            if( foundIndex !== -1 ) setParamCapacity(capacities[foundIndex]);
            else setParamCapacity(capacities[0]);
        } else {
            setCapacityEnabled(false);
        }
    }

    const onCapacityChange = (event, value) => {
        setParamCapacity(value);
        const powerValues = getPossiblePower(value);
        if (powerValues.length > 0) {
            const foundIndex = powerValues.findIndex(entry => entry === paramPower );
            if( foundIndex !== -1 ) setParamPower(powerValues[foundIndex]);
            else setParamPower(powerValues[0]);
        }
    }

    const getPossibleCapacities = useCallback((selectedPower) => {
        return Object.values(configurations)
            .filter(config => config.power === selectedPower)
            .flatMap(config => config.capacity)
            .sort((a, b) => a - b);
    }, [configurations]);

    const getPossiblePower = useCallback((selectedCapacity) => {
        return Object.values(configurations)
            .filter(config => config.capacity === selectedCapacity)
            .flatMap(config => config.power)
            .sort((a, b) => a - b);
    }, [configurations]);

    useEffect(() => {
        if (paramCapacity !== null && paramPower !== null) {
            updateConfiguration(paramPower, paramCapacity);
        }
    }, [paramCapacity, paramPower, updateConfiguration]);

    useEffect(() => {
        if (matchedConfiguration) {
            setResultCapacity(`${matchedConfiguration.capacity.toLocaleString('de-DE')} MWh`);
            setResultPower(`${matchedConfiguration.power.toLocaleString('de-DE')} MW`);
            setResultProductName(`${matchedConfiguration.productName}`);
            setCapacityEnabled(true);
        } else {
            setResultCapacity('--- kWh');
            setResultPower('--- MW');
            setResultProductName(`---`);
            setCapacityEnabled(false);
        }
    }, [matchedConfiguration]);

    const getCurrentPowerMarkIndex = () => {
        const index = marksPower.findIndex(entry => entry.value === paramPower);
        return index !== -1 ? index : 0;
    }

    const getCurrentCapacityMarkIndex = () => {
        const index = marksCapacity.findIndex(entry => entry.value === paramCapacity);
        return index !== -1 ? index : 0;
    }
    
    const increasePower = (event) => {
        const markIndex = getCurrentPowerMarkIndex();
        if( !marksPower[markIndex+1] ) return;

        onPowerChange(event, marksPower[markIndex+1].value);
    };
    
    const decreasePower = (event) => {
        const markIndex = getCurrentPowerMarkIndex();
        if( !marksPower[markIndex-1] ) return;

        onPowerChange(event, marksPower[markIndex-1].value);
    };
    
    const increaseCapacity = (event) => {
        const markIndex = getCurrentCapacityMarkIndex();
        if( !marksCapacity[markIndex+1] ) return;

        onCapacityChange(event, marksCapacity[markIndex+1].value);
    };
    
    const decreaseCapacity = (event) => {
        const markIndex = getCurrentCapacityMarkIndex();
        if( !marksCapacity[markIndex-1] ) return;

        onCapacityChange(event, marksCapacity[markIndex-1].value);
    };

    const toggleContactForm = (event) => {
        document.getElementsByTagName('body')[0].className = 'contact';
        document.getElementById('contactForm').scrollIntoView({ behavior: 'smooth' });
    }

    const render = () => {
        return (
            <ThemeProvider theme={LightTheme}>
                <ConfigAreaRoot elevation={3} className="ConfigArea-root">
                    <Typography variant='h3' align='center' className="ConfigArea-headline">Your Configuration</Typography>
                    <Box className="ConfigArea-controls">
                        <Box className="ConfigArea-control">
                            <Typography>Power (MW)</Typography>
                            <Stack className='Slider-stack' gap={0} direction='row' alignItems='start'>
                                <IconButton onClick={decreasePower} aria-label='decrease power' title='decrease power' disabled={paramPower <= minPower}><RemoveCircleOutlineOutlined sx={{ fontSize: 28 }}/></IconButton>
                                <Slider
                                    aria-label="Power (MW)"
                                    getAriaValueText={formatPowerValue}
                                    valueLabelFormat={formatPowerValue}
                                    value={paramPower}
                                    step={null}
                                    marks={marksPower}
                                    min={minPower}
                                    max={maxPower}
                                    valueLabelDisplay='off'
                                    onChange={onPowerChange}
                                />
                                <IconButton onClick={increasePower} aria-label='increase power' title='increase power' disabled={paramPower >= maxPower}><AddCircleOutlineOutlined sx={{ fontSize: 28 }}/></IconButton>
                            </Stack>
                        </Box>
                        <Box className="ConfigArea-control">
                            <Typography component='h4'>Capacity (MWh)</Typography>
                            <Stack className='Slider-stack' gap={0} direction='row' alignItems='start'>
                                <IconButton onClick={decreaseCapacity} aria-label='decrease capacity' title='decrease capacity' disabled={paramCapacity <= minCapacity}><RemoveCircleOutlineOutlined sx={{ fontSize: 28 }}/></IconButton>
                                <Slider
                                    aria-label="Capacity (kWh)"
                                    getAriaValueText={formatCapacityValue}
                                    valueLabelFormat={formatCapacityValue}
                                    value={paramCapacity}
                                    disabled={!capacityEnabled}
                                    step={null}
                                    marks={marksCapacity}
                                    min={minCapacity}
                                    max={maxCapacity}
                                    valueLabelDisplay='off'
                                    onChange={onCapacityChange}
                                />
                                <IconButton onClick={increaseCapacity} aria-label='increase capacity' title='increase capacity' disabled={paramCapacity >= maxCapacity}><AddCircleOutlineOutlined sx={{ fontSize: 28 }}/></IconButton>
                            </Stack>
                        </Box>
                        <Box className="ConfigArea-control">
                            <Typography>Full cycles per day</Typography>
                            <Stack className='Slider-stack' gap={0} direction='row' alignItems='start'>
                                <IconButton onClick={() => setParamFce(paramFce - STEP_FCE)} aria-label='decrease full cycles' title='decrease full cycles' disabled={paramFce <= MIN_FCE}><RemoveCircleOutlineOutlined sx={{ fontSize: 28 }}/></IconButton>
                                <Slider
                                    aria-label="Full Cycle Equivalent (daily)"
                                    name='full_cycles'
                                    value={paramFce}
                                    getAriaValueText={formatFceValue}
                                    valueLabelFormat={formatFceValue}
                                    step={STEP_FCE}
                                    marks={marksFce}
                                    valueLabelDisplay='off'
                                    min={MIN_FCE}
                                    max={MAX_FCE}
                                    onChange={(event, value) => setParamFce(value)}
                                />
                                <IconButton onClick={() => setParamFce(paramFce + STEP_FCE)} aria-label='increase full cycles' title='increase full cycles' disabled={paramFce >= MAX_FCE}><AddCircleOutlineOutlined sx={{ fontSize: 28 }}/></IconButton>
                            </Stack>
                        </Box>
                    </Box>
                    <Box className="ConfigArea-controls ConfigArea-controls-print">
                        <Typography variant='h4' component="div">Selected values</Typography>
                        <Box className="ConfigArea-control">
                            <Typography>Power (MW):</Typography><div>{paramPower}</div>
                        </Box>
                        <Box className="ConfigArea-control">
                            <Typography>Capacity (MWh):</Typography><div>{paramCapacity}</div>
                        </Box>
                        <Box className="ConfigArea-control">
                            <Typography>Full cycles per day:</Typography><div>{paramFce}</div>
                        </Box>
                    </Box>
                    <Box className="ConfigArea-calc">
                        <div>
                            <Typography variant="h3" component="div"><span>{resultProductName}</span></Typography>
                            <Typography variant="body1" component="div">Matching variant</Typography>
                        </div>
                        <div>
                            <Typography variant="h3" component="div"><span>{resultCapacity}</span></Typography>
                            <Typography variant="body1" component="div">Nominal Capacity</Typography>
                        </div>
                        <div>
                            <Typography variant="h3" component="div"><span>{resultPower}</span></Typography>
                            <Typography variant="body1" component="div">Continuous power</Typography>
                        </div>
                        <input type="hidden" name="matching_variant" value={resultProductName} />
                        <input type="hidden" name="capacity" value={resultCapacity} />
                        <input type="hidden" name="power" value={resultPower} />
                    </Box>
                    <Box className="ConfigArea-cta">
                        <Button id="button-print" color="primary" variant="contained" size='large' startIcon={<SendIcon />} onClick={toggleContactForm} disabled={matchedConfiguration === null}>Send your inquiry</Button>
                        <Typography className="cta-print" variant='body1' component="p">energy-sales@mercedes-benz.com</Typography>
                    </Box>
                </ConfigAreaRoot>
            </ThemeProvider>
        )
    };

    return render();
};

ConfigArea.propTypes = {};

export default ConfigArea;